/**
 * BulkRecordingModeModal — apply recording settings to many cameras at once.
 *
 * Nobody configures 64 cameras one at a time, so this is not optional polish.
 *
 * Shared by both dashboards. It previously lived only in local-node, which is why the cloud
 * camera list had no bulk recording action at all; the transport differs (local posts
 * straight to the backend, cloud goes through the tunnel) so the caller owns `onApply` and
 * this component knows nothing about either.
 *
 * WHY EVERY FIELD IS OPT-IN: a bulk edit sends ONLY the fields the operator ticked. A form
 * that always submitted every value would silently flatten each camera's individually-tuned
 * pre-roll, sensitivity and retention the moment someone bulk-changed the mode. The backend
 * upsert is already patch-style; this keeps the UI honest about that.
 *
 * WHY THE TRIGGER SECTIONS AUTO-OPEN: picking "Event-based" and getting no way to say WHICH
 * detections is the bug this section exists to fix — the cameras would land in event mode
 * carrying whatever event types they happened to have. Choosing a mode now ticks the
 * matching trigger group, so the settings that mode depends on are in front of the operator
 * instead of hidden behind another checkbox.
 */
import { useState } from 'react';
import { XMarkIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import {
  MODE_LABELS, EVENT_TYPE_LABELS, TIMER_DRIVEN_EVENT_TYPES, MOTION_SOURCE_LABELS,
  FIELD_HINTS, modeUsesEvents, modeUsesMotion,
} from './recordingVocab';

const inputCls =
  'w-full rounded-lg border border-border bg-muted px-3 py-2 text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand-ring disabled:opacity-40';
const labelCls = 'text-[11px] font-medium text-muted-foreground mb-1 block';
const hintCls = 'mt-1 text-[10.5px] leading-snug text-muted-foreground';
const groupCls = 'space-y-2 border-t border-border pt-4';
const checkCls = 'rounded border-border text-brand focus:ring-brand-ring';

export interface BulkRecordingPatch {
  enabled?: boolean;
  mode?: string;
  event_types?: string[];
  min_confidence?: number;
  motion_source?: string;
  motion_sensitivity?: number;
  motion_min_duration_ms?: number;
  motion_idle_timeout_ms?: number;
  pre_roll_seconds?: number;
  post_roll_seconds?: number;
  max_clip_seconds?: number;
  retention_days?: number | null;
}

export interface BulkRecordingModeModalProps {
  isOpen: boolean;
  onClose: () => void;
  cameraIds?: string[];
  options?: { modes?: string[]; motion_sources?: string[]; event_types?: string[] };
  defaults?: { max_pre_roll_seconds?: number; retention_days?: number };
  eventModeAvailable?: boolean;
  motionModeAvailable?: boolean;
  onApply: (patch: BulkRecordingPatch) => void | Promise<void>;
}

export default function BulkRecordingModeModal({
  isOpen,
  onClose,
  cameraIds = [],
  options = {},
  defaults = {},
  eventModeAvailable = true,
  motionModeAvailable = true,
  onApply,
}: BulkRecordingModeModalProps) {
  // Each field carries its own "include this in the patch" flag — see the note above.
  const [applyEnabled, setApplyEnabled] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [applyMode, setApplyMode] = useState(true);
  const [mode, setMode] = useState('continuous');

  const [applyEvents, setApplyEvents] = useState(false);
  const [eventTypes, setEventTypes] = useState<string[]>([]);
  const [minConfidence, setMinConfidence] = useState(0);

  const [applyMotion, setApplyMotion] = useState(false);
  const [motionSource, setMotionSource] = useState('auto');
  const [motionSensitivity, setMotionSensitivity] = useState(5);
  // Kept as strings: these are bound to number inputs, whose value is always a string.
  // Coerced once when the patch is built.
  const [motionMinDuration, setMotionMinDuration] = useState('800');
  const [motionIdleTimeout, setMotionIdleTimeout] = useState('5000');

  const [applyWindow, setApplyWindow] = useState(false);
  const [preRoll, setPreRoll] = useState('10');
  const [postRoll, setPostRoll] = useState('30');
  const [maxClip, setMaxClip] = useState('300');

  const [applyRetention, setApplyRetention] = useState(false);
  const [retentionDays, setRetentionDays] = useState('');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const modes = options.modes || Object.keys(MODE_LABELS);
  const allEventTypes = options.event_types || Object.keys(EVENT_TYPE_LABELS);
  const motionSources = options.motion_sources || Object.keys(MOTION_SOURCE_LABELS);
  const maxPreRoll = defaults.max_pre_roll_seconds ?? 600;
  const count = cameraIds.length;

  const modeUnavailable = (m: string) => {
    if (m === 'continuous') return false;
    if (m === 'event') return !eventModeAvailable;
    if (m === 'motion') return !motionModeAvailable;
    return !eventModeAvailable || !motionModeAvailable;
  };

  /**
   * Picking a mode opens the settings that mode actually depends on, and closes the ones it
   * cannot use — leaving "Set motion" ticked while switching everything to Event-based would
   * write motion settings no camera in that mode will ever read.
   */
  const chooseMode = (m: string) => {
    setMode(m);
    setApplyEvents(modeUsesEvents(m));
    setApplyMotion(modeUsesMotion(m));
    if (m !== 'continuous') setApplyWindow(true);
  };

  const toggleEventType = (type: string) =>
    setEventTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );

  // Trigger sections stay reachable when the mode isn't being changed: the operator may be
  // retuning cameras that are already in a triggered mode.
  const showEvents = !applyMode || modeUsesEvents(mode);
  const showMotion = !applyMode || modeUsesMotion(mode);
  const showWindow = !applyMode || mode !== 'continuous';

  const nothingSelected =
    !applyEnabled && !applyMode && !applyEvents && !applyMotion && !applyWindow && !applyRetention;

  async function handleApply() {
    setBusy(true);
    setError(null);
    try {
      const patch: BulkRecordingPatch = {};
      if (applyEnabled) patch.enabled = enabled;
      if (applyMode) patch.mode = mode;
      if (applyEvents && showEvents) {
        patch.event_types = eventTypes;
        patch.min_confidence = minConfidence;
      }
      if (applyMotion && showMotion) {
        patch.motion_source = motionSource;
        patch.motion_sensitivity = motionSensitivity;
        patch.motion_min_duration_ms = Number(motionMinDuration);
        patch.motion_idle_timeout_ms = Number(motionIdleTimeout);
      }
      if (applyWindow && showWindow) {
        patch.pre_roll_seconds = Number(preRoll);
        patch.post_roll_seconds = Number(postRoll);
        patch.max_clip_seconds = Number(maxClip);
      }
      if (applyRetention) {
        patch.retention_days = retentionDays === '' ? null : Number(retentionDays);
      }
      await onApply(patch);
      onClose();
    } catch (err: any) {
      const data = err.response?.data;
      setError(data?.details?.[0]?.message || data?.error || err.message || 'Failed to apply');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="w-full max-w-lg overflow-hidden rounded-2xl border border-border bg-card shadow-modal">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-border px-6 py-5">
          <div className="flex items-center gap-3">
            {/* Inline tint, not `bg-brand/10`: Tailwind v3 drops opacity modifiers on
                colours defined as bare `var()`, so that utility compiles to nothing. */}
            <div
              className="flex h-10 w-10 items-center justify-center rounded-lg"
              style={{ backgroundColor: 'color-mix(in srgb, var(--brand) 12%, transparent)' }}
            >
              <VideoCameraIcon className="h-5 w-5 text-brand" />
            </div>
            <div>
              <h2 className="text-[15px] font-semibold text-foreground">Recording Settings</h2>
              <p className="text-[12px] text-muted-foreground">
                Applying to {count} camera{count !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[65vh] space-y-5 overflow-y-auto px-6 py-5">
          <p className="text-[11px] text-muted-foreground">
            Only the settings you tick are changed. Everything else keeps each camera&apos;s
            existing value.
          </p>

          {/* Enabled */}
          <div className="space-y-2">
            <label className="flex cursor-pointer items-center gap-2 text-[13px] font-medium text-foreground">
              <input
                type="checkbox"
                checked={applyEnabled}
                onChange={(e) => setApplyEnabled(e.target.checked)}
                className={checkCls}
              />
              Set recording on/off
            </label>
            {applyEnabled && (
              <div className="ml-6">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={enabled}
                    onClick={() => setEnabled((v) => !v)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2 ${
                      enabled ? 'bg-brand' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className="text-[12px] text-muted-foreground">
                    {enabled ? 'Recording enabled' : 'Recording disabled'}
                  </span>
                </div>
                <p className={hintCls}>{FIELD_HINTS.enabled}</p>
              </div>
            )}
          </div>

          {/* Mode */}
          <div className={groupCls}>
            <label className="flex cursor-pointer items-center gap-2 text-[13px] font-medium text-foreground">
              <input
                type="checkbox"
                checked={applyMode}
                onChange={(e) => setApplyMode(e.target.checked)}
                className={checkCls}
              />
              Set recording mode
            </label>
            {applyMode && (
              <div className="ml-6 space-y-1.5">
                {modes.map((m) => {
                  const unavailable = modeUnavailable(m);
                  const meta = MODE_LABELS[m] || { label: m, hint: '' };
                  return (
                    <label
                      key={m}
                      className={`flex items-start gap-2 text-[12px] ${
                        unavailable
                          ? 'cursor-not-allowed text-muted-foreground opacity-50'
                          : 'cursor-pointer text-foreground'
                      }`}
                    >
                      <input
                        type="radio"
                        name="bulk-recording-mode"
                        checked={mode === m}
                        disabled={unavailable}
                        onChange={() => chooseMode(m)}
                        className="mt-0.5 text-brand focus:ring-brand-ring"
                      />
                      <span>
                        {meta.label}
                        {unavailable && ' (available in a later release)'}
                        {meta.hint && (
                          <span className="block text-[11px] text-muted-foreground">{meta.hint}</span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Event triggers — which detections keep footage */}
          {showEvents && (
            <div className={groupCls}>
              <label className="flex cursor-pointer items-center gap-2 text-[13px] font-medium text-foreground">
                <input
                  type="checkbox"
                  checked={applyEvents}
                  onChange={(e) => setApplyEvents(e.target.checked)}
                  className={checkCls}
                />
                Set event triggers
              </label>
              {applyEvents && (
                <div className="ml-6 space-y-3">
                  <p className="text-[11px] text-muted-foreground">{FIELD_HINTS.event_types}</p>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {allEventTypes.map((type) => {
                      const timerDriven = TIMER_DRIVEN_EVENT_TYPES.has(type);
                      return (
                        <label
                          key={type}
                          title={timerDriven
                            ? 'Reports on a timer (about every 60s), so this keeps footage continuously'
                            : undefined}
                          className="flex cursor-pointer items-center gap-2 text-[12px] text-foreground"
                        >
                          <input
                            type="checkbox"
                            checked={eventTypes.includes(type)}
                            onChange={() => toggleEventType(type)}
                            className={checkCls}
                          />
                          <span>
                            {EVENT_TYPE_LABELS[type] || type}
                            {timerDriven && <span className="text-muted-foreground"> ⏱</span>}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  {/* Selecting a timer-driven category is a legitimate choice, but it means
                      "keep everything" — say so before the operator discovers it from the disk. */}
                  {eventTypes.some((t) => TIMER_DRIVEN_EVENT_TYPES.has(t)) && (
                    <p className="text-[11px] text-amber-600 dark:text-amber-400">
                      ⏱ People counting reports about every 60 seconds, so footage will be kept
                      almost continuously. Use <span className="font-medium">Person</span> instead
                      to keep footage only when someone is actually detected.
                    </p>
                  )}
                  <div>
                    <label className={labelCls}>
                      Minimum confidence — {minConfidence.toFixed(2)}
                    </label>
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.05}
                      value={minConfidence}
                      onChange={(e) => setMinConfidence(Number(e.target.value))}
                      className="w-full accent-brand"
                    />
                    <p className={hintCls}>{FIELD_HINTS.min_confidence}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Motion */}
          {showMotion && (
            <div className={groupCls}>
              <label className="flex cursor-pointer items-center gap-2 text-[13px] font-medium text-foreground">
                <input
                  type="checkbox"
                  checked={applyMotion}
                  onChange={(e) => setApplyMotion(e.target.checked)}
                  className={checkCls}
                />
                Set motion detection
              </label>
              {applyMotion && (
                <div className="ml-6 space-y-3">
                  <div>
                    <label className={labelCls}>Source</label>
                    <select
                      value={motionSource}
                      onChange={(e) => setMotionSource(e.target.value)}
                      className={inputCls}
                    >
                      {motionSources.map((s) => (
                        <option key={s} value={s}>{MOTION_SOURCE_LABELS[s] || s}</option>
                      ))}
                    </select>
                    <p className={hintCls}>{FIELD_HINTS.motion_source}</p>
                  </div>
                  <div>
                    <label className={labelCls}>Sensitivity — {motionSensitivity} / 10</label>
                    <input
                      type="range"
                      min={1}
                      max={10}
                      step={1}
                      value={motionSensitivity}
                      onChange={(e) => setMotionSensitivity(Number(e.target.value))}
                      className="w-full accent-brand"
                    />
                    <p className={hintCls}>{FIELD_HINTS.motion_sensitivity}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Min duration (ms)</label>
                      <input
                        type="number"
                        min={0}
                        max={60000}
                        step={100}
                        value={motionMinDuration}
                        onChange={(e) => setMotionMinDuration(e.target.value)}
                        className={inputCls}
                      />
                      <p className={hintCls}>{FIELD_HINTS.motion_min_duration}</p>
                    </div>
                    <div>
                      <label className={labelCls}>Quiet timeout (ms)</label>
                      <input
                        type="number"
                        min={0}
                        max={600000}
                        step={500}
                        value={motionIdleTimeout}
                        onChange={(e) => setMotionIdleTimeout(e.target.value)}
                        className={inputCls}
                      />
                      <p className={hintCls}>{FIELD_HINTS.motion_idle_timeout}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Clip window — only meaningful for triggered modes */}
          {showWindow && (
            <div className={groupCls}>
              <label className="flex cursor-pointer items-center gap-2 text-[13px] font-medium text-foreground">
                <input
                  type="checkbox"
                  checked={applyWindow}
                  onChange={(e) => setApplyWindow(e.target.checked)}
                  className={checkCls}
                />
                Set clip window
              </label>
              {applyWindow && (
                <div className="ml-6 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Pre-roll (s, max {maxPreRoll})</label>
                      <input
                        type="number"
                        min={0}
                        max={maxPreRoll}
                        value={preRoll}
                        onChange={(e) => setPreRoll(e.target.value)}
                        className={inputCls}
                      />
                      <p className={hintCls}>{FIELD_HINTS.pre_roll}</p>
                    </div>
                    <div>
                      <label className={labelCls}>Post-roll (s)</label>
                      <input
                        type="number"
                        min={0}
                        max={3600}
                        value={postRoll}
                        onChange={(e) => setPostRoll(e.target.value)}
                        className={inputCls}
                      />
                      <p className={hintCls}>{FIELD_HINTS.post_roll}</p>
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Max clip (s)</label>
                    <input
                      type="number"
                      min={30}
                      max={86400}
                      value={maxClip}
                      onChange={(e) => setMaxClip(e.target.value)}
                      className={inputCls}
                    />
                    <p className={hintCls}>{FIELD_HINTS.max_clip}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Retention */}
          <div className={groupCls}>
            <label className="flex cursor-pointer items-center gap-2 text-[13px] font-medium text-foreground">
              <input
                type="checkbox"
                checked={applyRetention}
                onChange={(e) => setApplyRetention(e.target.checked)}
                className={checkCls}
              />
              Set retention
            </label>
            {applyRetention && (
              <div className="ml-6">
                <label className={labelCls}>Keep for (days)</label>
                <input
                  type="number"
                  min={0}
                  placeholder={`System default (${defaults.retention_days ?? 30})`}
                  value={retentionDays}
                  onChange={(e) => setRetentionDays(e.target.value)}
                  className={inputCls}
                />
                <p className={hintCls}>{FIELD_HINTS.retention_days}</p>
              </div>
            )}
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] font-medium text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-border px-6 py-4">
          <button
            onClick={onClose}
            disabled={busy}
            className="rounded-lg px-4 py-2 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-muted disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={busy || nothingSelected}
            title={nothingSelected ? 'Tick at least one setting to change' : undefined}
            className="rounded-lg bg-brand px-4 py-2 text-[13px] font-medium text-white shadow-sm transition-colors hover:bg-brand-dark disabled:opacity-50"
          >
            {busy ? 'Applying…' : `Apply to ${count} Camera${count !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}
